import { useState } from 'react';
import { Circle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { cn } from '../../lib/utils';

const PRESET_RADII = [
  { label: '50m', value: 50 },
  { label: '100m', value: 100 },
  { label: '200m', value: 200 },
  { label: '300m', value: 300 },
  { label: '500m', value: 500 },
];

const PRESET_COLORS = [
  { label: 'Gefahr', value: '#ef4444', name: 'Rot' },
  { label: 'Warnung', value: '#f97316', name: 'Orange' },
  { label: 'Vorsicht', value: '#eab308', name: 'Gelb' },
  { label: 'Sicher', value: '#22c55e', name: 'Grün' },
  { label: 'Info', value: '#3b82f6', name: 'Blau' },
];

export const CircleDrawModal = ({ open, onOpenChange, onConfirm, position }) => {
  const [radius, setRadius] = useState(100);
  const [color, setColor] = useState('#ef4444');
  const [label, setLabel] = useState('');
  const [dashed, setDashed] = useState(false);

  const handleConfirm = () => {
    onConfirm({
      radius,
      color,
      label: label.trim() || 'Umkreis',
      dashed
    });
    // Reset for next use
    setRadius(100);
    setColor('#ef4444');
    setLabel('');
    setDashed(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="circle-draw-modal">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2 text-lunefire-charcoal dark:text-white">
            <Circle className="w-5 h-5 text-lunefire-red" />
            Umkreis zeichnen
          </DialogTitle>
          <DialogDescription className="font-body">
            Definieren Sie den Radius und die Eigenschaften des Kreises.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Radius */}
          <div className="space-y-2">
            <Label>Radius (Meter)</Label>
            <div className="flex gap-2 flex-wrap">
              {PRESET_RADII.map((preset) => (
                <Button
                  key={preset.value}
                  variant={radius === preset.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setRadius(preset.value)}
                  className={cn(
                    "rounded-md",
                    radius === preset.value && "bg-lunefire-red hover:bg-lunefire-red/90"
                  )}
                  data-testid={`radius-preset-${preset.value}`}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <Input
              type="number"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              min={10}
              max={10000}
              className="font-mono mt-2"
              data-testid="radius-input"
            />
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label>Farbe</Label>
            <div className="flex gap-2 flex-wrap">
              {PRESET_COLORS.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => setColor(preset.value)}
                  className={`w-8 h-8 rounded-sm border-2 transition-all ${
                    color === preset.value ? 'border-foreground scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: preset.value }}
                  title={preset.name}
                  data-testid={`color-preset-${preset.name.toLowerCase()}`}
                />
              ))}
            </div>
          </div>

          {/* Label */}
          <div className="space-y-2">
            <Label htmlFor="circle-label">Bezeichnung</Label>
            <Input
              id="circle-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="z.B. Innere Absperrung"
              data-testid="circle-label-input"
            />
          </div>

          {/* Dashed */}
          <div className="flex items-center justify-between">
            <Label htmlFor="dashed-switch">Gestrichelte Linie</Label>
            <Switch
              id="dashed-switch"
              checked={dashed}
              onCheckedChange={setDashed}
              data-testid="dashed-switch"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="hover:bg-lunefire-red/10">
            Abbrechen
          </Button>
          <Button onClick={handleConfirm} data-testid="confirm-circle-btn" className="bg-lunefire-red hover:bg-lunefire-red/90 text-white font-display uppercase tracking-wider">
            <Circle className="w-4 h-4 mr-2" />
            Erstellen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CircleDrawModal;
